import { CircularProgress, Grid, Typography } from '@mui/material';
import React, { Suspense, useEffect, useState } from 'react';
import ContentEditable, { ContentEditableEvent } from 'react-contenteditable';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  BoardType,
  ParamsPanelSelection,
  RenderableMetadata,
  Units,
} from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import EditorDetailsCard from 'designer/EditorDetailsCard';
import EditorParamsCard from 'designer/EditorParamsCard';
import EditorPreviewCard from 'designer/EditorPreviewCard';
import UnitSelector from 'settings/UnitSelector';
import { useUpdateBoard } from 'state/recoilBoardActions';

function Editor<T extends BoardType>(props: { boardInstance: BoardBase<T> }) {
  const { boardInstance } = props;

  const updateBoard = useUpdateBoard<T>();

  const [previewSelection, setPreviewSelection] =
    useState<null | RenderableMetadata<T>>(null);
  const [paramsPanelSelection, setParamsPanelSelection] =
    useState<null | ParamsPanelSelection<T>>(null);

  const onNameChange = (event: ContentEditableEvent) => {
    // Somehow contenteditable can end up with html in it
    // IDK how this happens but this fixes it.
    const nameUnsafe = event.target.value;
    const el = document.createElement('div');
    el.innerHTML = nameUnsafe;
    const nameSafe = el.innerText;
    boardInstance && updateBoard(boardInstance.rename(nameSafe));
  };

  const onBoardUnitsChange = (units: Units) => {
    boardInstance &&
      updateBoard(
        boardInstance
          .updateUnits(units)
          .updateParams(
            boardInstance.scaleParams(
              boardInstance.params,
              units === Units.IN ? 1 / 25.4 : 25.4
            )
          )
      );
  };

  const onPreviewSelectionChange = (
    selection: null | RenderableMetadata<T>
  ) => {
    setPreviewSelection(selection);
    setParamsPanelSelection(
      selection == null
        ? null
        : boardInstance.previewSelectionToParamsPanelSelection(selection)
    );
  };

  const onParamsPanelSelectionChange = (
    selection: null | ParamsPanelSelection<T>
  ) => {
    setParamsPanelSelection(selection);
    setPreviewSelection(
      selection == null
        ? null
        : boardInstance.paramsPanelSelectionToPreviewSelection(selection)
    );
  };

  // Reset selection when changing boards
  useEffect(() => {
    setParamsPanelSelection(null);
    setPreviewSelection(null);
  }, [boardInstance.id]);

  return (
    <Suspense fallback={<CircularProgress />}>
      <DndProvider backend={HTML5Backend}>
        <Grid container direction="column" spacing={2}>
          <Grid container item justifyContent="space-between" direction="row">
            <Typography
              variant="h4"
              color="inherit"
              style={{ lineHeight: 1.5 }}
            >
              <ContentEditable
                html={boardInstance.name}
                onChange={onNameChange}
                spellCheck={false}
              />
            </Typography>

            <UnitSelector
              isCompact={true}
              value={boardInstance.units}
              onChange={onBoardUnitsChange}
            />
          </Grid>

          <Grid item>
            <Grid container spacing={2} wrap="wrap-reverse">
              <Grid item style={{ flex: 99999 }}>
                <Grid container direction="column" spacing={2}>
                  <Grid item>
                    <EditorParamsCard
                      boardInstance={boardInstance}
                      onSelectionChange={onParamsPanelSelectionChange}
                      selection={paramsPanelSelection}
                    />
                  </Grid>
                  <Grid item>
                    <EditorDetailsCard boardInstance={boardInstance} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item style={{ flex: 1 }}>
                <EditorPreviewCard
                  boardInstance={boardInstance}
                  onSelectionChange={onPreviewSelectionChange}
                  selection={previewSelection}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DndProvider>
    </Suspense>
  );
}

export default Editor;
